'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Award, FileCheck, CheckCircle2, Factory, PackageSearch, Beaker, FileText, Download, ArrowRight, ChevronDown } from 'lucide-react';
import CTASection from '@/components/sections/CTASection';

const CertificationsContent = () => {
    const [openFaq, setOpenFaq] = useState(0);

    const certs = [
        {
            title: "ISO 9001:2015 (QMS)",
            authority: "Quality Management System",
            number: "QMS-260108-180159",
            validity: "Active Compliance",
            relevance: "Ensures our quality management systems consistently meet customer and regulatory requirements across all production lines.",
            icon: <Award size={32} className="text-blue-600" />,
            pdfLink: "/certifications/ROYAL PRIME LABELS QMS_260108_180159.pdf"
        },
        {
            title: "ISO 14001:2015 (EMS)",
            authority: "Environmental Management System",
            number: "EMS-260108-180222",
            validity: "Active Compliance",
            relevance: "Verifies our compliance with global environmental standards, including active waste management and energy reduction protocols.",
            icon: <Factory size={32} className="text-emerald-600" />,
            pdfLink: "/certifications/ROYAL PRIME LABELS EMS_260108_180222.pdf"
        },
        {
            title: "ISO 45001:2018 (OHSMS)",
            authority: "Occupational Health & Safety System",
            number: "OHSMS-260108-180243",
            validity: "Active Compliance",
            relevance: "Verifies our adherence to rigorous workplace health and safety protocols to protect our factory floor and team.",
            icon: <ShieldCheck size={32} className="text-orange-600" />,
            pdfLink: "/certifications/ROYAL PRIME LABELS OHSMS_260108_180243.pdf"
        },
        {
            title: "FSC® Certified (CoC)",
            authority: "Forest Stewardship Council®",
            number: "FSC-CoC-4D2C4D",
            validity: "Active Compliance",
            relevance: "Guarantees that all paper-based packaging substrates and materials are sourced from responsibly managed forests.",
            icon: <FileCheck size={32} className="text-green-600" />,
            pdfLink: "/certifications/4d2c4d69-2354-4fdc-8606-e4b3550d2fdb.pdf"
        }
    ];

    const qaWorkflow = [
        { title: "Incoming Material Inspection", desc: "Rigorous testing of raw substrates and adhesives before production begins." },
        { title: "In-line Video Inspection", desc: "100% automated defect detection during the high-speed printing process." },
        { title: "Batch Consistency Validation", desc: "Spectrophotometer checks to ensure perfect color matching across massive production runs." },
        { title: "Final Dispatch Audit", desc: "Manual and automated counting, barcode verification, and secure packaging checks." }
    ];

    const faqs = [
        { q: "Can we request copies of your certification documents?", a: "Yes, fully verified PDF copies of our ISO, GMP, and FSC certifications can be provided to your procurement or compliance teams upon request or downloaded directly via our portal." },
        { q: "Are your labels safe for direct food contact?", a: "We offer specialized food-grade substrates and low-migration inks that comply with FDA and FSSAI regulations for direct and indirect food contact." },
        { q: "How do you ensure color consistency across different batches?", a: "We utilize closed-loop color management systems and spectrophotometers to maintain strict delta-E tolerances, ensuring your brand colors remain exactly the same from batch 1 to batch 100." },
        { q: "What is your quality defect rate?", a: "Thanks to our 100% automated video inspection systems on our flexographic presses, our defect rate is near zero. Any defective labels are automatically identified and spliced out before shipping." }
    ];

    return (
        <main className="w-full bg-white pt-24 pb-0">
            {/* Hero Section */}
            <section className="relative w-full bg-[#0A1F33] py-20 px-6">
                <div className="absolute inset-0 z-0 opacity-20">
                    <Image src="/images/advanced_label_infrastructure.png" alt="Factory Background" fill className="object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F33] to-[#0A1F33]/80" />
                </div>
                <div className="relative z-10 max-w-[1000px] mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                        Uncompromising Quality & Compliance
                    </h1>
                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        At Royal Prime Labels, we don't just print labels—we engineer trust. Explore the rigorous certifications and quality assurance protocols that make us the preferred partner for global Pharma, FMCG, and industrial brands.
                    </p>
                </div>
            </section>

            {/* Certifications Grid */}
            <section className="py-24 bg-slate-50 border-b border-slate-200 relative">
                <div className="max-w-[1240px] mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#1D2B4F] mb-4">Official Certifications</h2>
                        <p className="text-lg text-[#5A6782]">Internationally recognized standards of excellence.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {certs.map((cert, idx) => (
                            <motion.div 
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row gap-6"
                            >
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
                                        {cert.icon}
                                    </div>
                                </div>
                                <div className="flex flex-col flex-1">
                                    <h3 className="text-2xl font-bold text-[#1D2B4F] mb-1">{cert.title}</h3>
                                    <p className="text-sm font-semibold text-blue-600 mb-3">{cert.authority}</p>
                                    <p className="text-[#5A6782] mb-4 flex-1 leading-relaxed">{cert.relevance}</p>
                                    
                                    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-100 mt-auto">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Cert No.</span>
                                            <span className="text-sm font-mono text-slate-700">{cert.number}</span>
                                        </div>
                                        <div className="flex flex-col text-right">
                                            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Status</span>
                                            <span className="text-sm font-semibold text-emerald-600">{cert.validity}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Download Button */}
                                    {cert.pdfLink ? (
                                        <a 
                                            href={cert.pdfLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-6 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors text-sm font-bold shadow-sm"
                                        >
                                            <Download size={16} />
                                            Download PDF Certificate
                                        </a>
                                    ) : (
                                        <button className="mt-6 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-600 border border-slate-200 hover:border-blue-200 transition-colors text-sm font-bold">
                                            <Download size={16} />
                                            Request PDF Copy
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Compliance Matters & Industry Relevance */}
            <section className="py-24 bg-white">
                <div className="max-w-[1240px] mx-auto px-6">
                    <div className="flex flex-col lg:flex-row gap-16 items-center">
                        <div className="w-full lg:w-1/2">
                            <h2 className="text-3xl md:text-4xl font-bold text-[#1D2B4F] mb-6">Why Strict Compliance Matters</h2>
                            <p className="text-lg text-[#5A6782] mb-8 leading-relaxed">
                                In modern manufacturing, a label is no longer just a sticker. It is a critical component of product safety, regulatory compliance, and brand integrity. Our rigorous standards, supported by our <Link href="/infrastructure" className="text-blue-600 font-semibold hover:underline">state-of-the-art infrastructure</Link> and extensive <Link href="/services" className="text-blue-600 font-semibold hover:underline">service capabilities</Link>, guarantee:
                            </p>
                            <ul className="space-y-4 mb-8">
                                {[
                                    "Pharma Labeling Requirements: Legibility, moisture resistance, and strict anti-migration inks.",
                                    "Export Readiness: Compliance with global packaging standards (EU, FDA).",
                                    "Product Traceability: Flawless variable data printing for supply chain tracking.",
                                    "Safety Standards: Non-toxic materials safe for consumer contact."
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                                        <span className="text-slate-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        <div className="w-full lg:w-1/2 bg-slate-50 p-8 md:p-10 rounded-3xl border border-slate-200">
                            <h3 className="text-2xl font-bold text-[#1D2B4F] mb-6">Trusted Across Critical Industries</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    { name: "Pharmaceuticals", desc: "FDA & GMP aligned", icon: <Beaker size={24} className="text-blue-600" /> },
                                    { name: "Food & Beverage", desc: "Food-safe substrates", icon: <PackageSearch size={24} className="text-orange-500" /> },
                                    { name: "Cosmetics", desc: "Premium durability", icon: <ShieldCheck size={24} className="text-pink-500" /> },
                                    { name: "Logistics", desc: "Weather-proof tracking", icon: <Factory size={24} className="text-slate-600" /> }
                                ].map((ind, idx) => (
                                    <div key={idx} className="bg-white p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                                        <div className="p-2 bg-slate-50 rounded-lg">
                                            {ind.icon}
                                        </div>
                                        <div>
                                            <p className="font-bold text-[#1D2B4F]">{ind.name}</p>
                                            <p className="text-xs text-slate-500">{ind.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link href="/industries" className="inline-flex items-center gap-2 mt-8 text-blue-600 font-bold hover:text-blue-700 transition-colors">
                                View all industries <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Procurement Trust & QA Workflow */}
            <section className="py-24 bg-[#123A5C] text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-[url('/images/advanced_label_infrastructure.png')] bg-cover opacity-10 mix-blend-overlay"></div>
                <div className="max-w-[1240px] mx-auto px-6 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Procurement Confidence: The QA Workflow</h2>
                        <p className="text-lg text-slate-300 max-w-2xl mx-auto">We deploy multi-stage quality gates to ensure your supply chain receives zero defective labels.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {qaWorkflow.map((step, idx) => (
                            <div key={idx} className="bg-white/5 border border-white/10 p-8 rounded-2xl relative">
                                <div className="text-5xl font-black text-white/10 absolute top-4 right-6 pointer-events-none">
                                    0{idx + 1}
                                </div>
                                <FileText className="text-blue-400 mb-6" size={32} />
                                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                                <p className="text-slate-300 text-sm leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-white">
                <div className="w-full max-w-[800px] mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#1D2B4F] mb-6">Compliance FAQs</h2>
                        <p className="text-lg text-[#5A6782]">Answers for procurement and quality assurance teams.</p>
                    </div>
                    
                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                <button
                                    className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
                                    onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                                >
                                    <span className="font-bold text-left text-[#1D2B4F] text-lg">{faq.q}</span>
                                    <ChevronDown className={`transform transition-transform duration-300 text-blue-600 shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {openFaq === idx && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-6 pb-5 pt-2 text-[#5A6782] leading-relaxed border-t border-slate-100 bg-slate-50/50">
                                                {faq.a}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CTASection
                title="Require Verified Quality for Your Brand?"
                description="Connect with our quality assurance team to discuss your regulatory requirements, request documentation, or schedule a facility audit."
                buttonText="Contact QA Team"
                href="/contact"
            />
        </main>
    );
};

export default CertificationsContent;
